import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Grid,
  Typography,
  Button,
  Box,
} from "@mui/material";
import { useDrag, useDrop } from "react-dnd";
import { ItemTypes } from "./constants";
import { THEME_COLOR3 } from "../../../constants";
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete1.svg";
import { ReactComponent as EditIcon } from "../../../assets/images/edit.svg";
import { UNGROUPED } from "../../../constants";
import FeatureItem from "./FeatureItems";

const FeatureGroupCard = ({
  groupData,
  index,
  moveGroup,
  moveFeatureCard,
  handleEditGroup,
  handleDeleteGroup,
  handleFeatureAdd,
  handleFeatureEdit,
  handleFeatureDelete,
  collapseGroups,
  handleCollapseGroup,
  updateGroupAfterDrop,
}) => {
  const isUngrouped = groupData._id === UNGROUPED;

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.GROUP,
    item: () => {
      handleCollapseGroup();
      return ({ id: groupData._id, index, type: ItemTypes.GROUP });
    },
    canDrag: () => !isUngrouped,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [, drop] = useDrop({
    accept: [ItemTypes.GROUP, ItemTypes.FEATURE],
    hover: (item) => {
      if (isUngrouped && item.type === ItemTypes.GROUP) return;

      if (item.type === ItemTypes.GROUP) {
        if (item.groupIndex === index) return;
        moveGroup(item.index, index);
        // Update the item's index for future drags
        item.index = index;
      }
    },
    canDrop: (item) => !(isUngrouped && (item.type === ItemTypes.GROUP)),
    drop: (item, monitor) => {
      // // Handle dropping a card onto a list container
      if (item.type === ItemTypes.FEATURE && !monitor.didDrop()) {
        moveFeatureCard(item.id, item.index, groupData.features.length, item.groupId, item.groupIndex, groupData._id, index, isUngrouped);
        return { dropped: true };
      }

      if (item.type === ItemTypes.GROUP) updateGroupAfterDrop();
    },
  });

  const ref = useRef(null);

  const [showFeatures, setShowFeatures] = useState(false);
  useEffect(() => {
    if (!isUngrouped) setShowFeatures(false);
  }, [collapseGroups]);

  const handleEditClick = () => {
    handleEditGroup(groupData);
  }

  const handleDeleteClick = () => {
    handleDeleteGroup(groupData);
  }

  const handleViewClick = () => {
    setShowFeatures(!showFeatures);
  };

  const handleNewFeatureClick = () => {
    handleFeatureAdd(groupData._id);
  }
  drag(drop(ref));
  return (<Card
    ref={ref}
    sx={[styles.mainGrid, {
      opacity: isDragging ? 0.5 : 1,
    }]}
  >
    <Grid
      container
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      {/* Role Name */}
      <Typography
        sx={{
          fontFamily: "GilroyBold",
          fontSize: 14,
          color: "black",
          paddingTop: 0.8,
          paddingBottom: 0.8,
        }}
      >
        {groupData.groupName}
      </Typography>

      {/* Buttons */}
      <Grid item sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "1rem"

      }}>
        {groupData._id !== UNGROUPED &&
          <>
            <EditIcon
              width={24}
              height={24}
              onClick={handleEditClick}
            />
            <DeleteIcon
              width={24}
              height={24}
              onClick={handleDeleteClick}
            />
          </>
        }

        <Button
          variant="contained"
          sx={styles.viewButton}
          onClick={handleViewClick}
        >
          {showFeatures ? "Hide" : "View"} Features
        </Button>
      </Grid>
    </Grid>

    {/* Features Count */}
    <Typography
      sx={{
        fontFamily: "GilroySemiBold",
        fontSize: 12,
        color: THEME_COLOR3,
        marginTop: 1,
      }}
    >
      Features Count: {groupData.features?.length || 0}
    </Typography>

    {/* Features List */}
    {showFeatures && (
      <Grid mt={2}>
        <Typography>
          <Button
            variant="contained"
            sx={styles.viewButton}
            onClick={handleNewFeatureClick}
          >
            Add Features
          </Button>
        </Typography>
        <Box sx={styles.featuresGrid}>
          {groupData.features?.length > 0 ? groupData.features.map((feature, featureIndex) => (
            <FeatureItem
              feature={feature}
              key={feature._id}
              groupIndex={index}
              index={featureIndex}
              moveFeatureCard={moveFeatureCard}
              groupId={groupData._id}
              handleFeatureEdit={handleFeatureEdit}
              handleFeatureDelete={handleFeatureDelete}
              isUngrouped={isUngrouped}
            />
          )) : <Typography variant="body2" sx={{ opacity: 0.5, textAlign: 'center', height: '2rem' }}>
            No features in this group. Add or Drop a feature here.
          </Typography>
          }
        </Box>
      </Grid>
    )}
  </Card>
  );
};

export default FeatureGroupCard;

const styles = {
  mainGrid: {
    cursor: "pointer",
    borderRadius: 3,
    width: "100%",
    padding: "1rem",
  },
  editButton: {
    backgroundColor: "#000",
    color: "#FFF",
    fontFamily: "GilroyBold",
    fontSize: 12,
    marginRight: 1,
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
  deleteButton: {
    backgroundColor: "#d32f2f",
    color: "#FFF",
    fontFamily: "GilroyBold",
    fontSize: 12,
    marginRight: 1,
    "&:hover": {
      backgroundColor: "#c62828",
    },
  },
  viewButton: {
    backgroundColor: "#000",
    color: "#FFF",
    fontFamily: "GilroyBold",
    fontSize: 11,
    width: "10rem",
    "&:hover": {
      backgroundColor: "#000000",
    },
  },
  cancelButton: {
    color: "#000",
    fontFamily: "GilroyBold",
    fontSize: 12,
    "&:hover": {
      backgroundColor: "#1565c0",
      color: "#FFF",
    },
  },
  featuresGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '1rem',
    margin: 0,
    marginTop: '1rem',
  }
};
